import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";

import { cn } from "@/lib/utils";

/**
 * The one toggle switch in the product.
 *
 * ⚠️ There were three of these: this one, a hand-rolled `role="switch"` button in
 * `ManagerAccessPanel`, and a private copy of that button inside
 * `NotificationPreferencesPanel`. They were different sizes, so the profile screen showed two
 * visibly different switches on one page. The two hand-rolled ones are gone and this carries
 * their proportions — a 44px-wide target rather than 36px, which is also the smallest thing
 * Apple and Google will call a touch target.
 *
 * ⚠️ Radix, so the accessibility is not something each call site has to remember: it renders a
 * real `role="switch"` with `aria-checked`, responds to Space and Enter, and participates in
 * the tab order. A `<div onClick>` does none of that, and a settings screen full of them is
 * unusable with a keyboard.
 *
 * Every call site passes either an `id` paired with a `<label htmlFor>` or an `aria-label`.
 * A switch whose only label is the sentence next to it is unlabelled to a screen reader.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={cn(
      "peer group inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
      "transition-colors duration-200 ease-out",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      "disabled:cursor-not-allowed disabled:opacity-50",
      // The off state is `--input`, the same border colour the rest of the form uses, so an
      // unset switch reads as an empty control rather than as something switched off in anger.
      "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      "hover:data-[state=unchecked]:bg-input/80 disabled:hover:data-[state=unchecked]:bg-input",
      className,
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={cn(
        "pointer-events-none block h-5 w-5 rounded-full bg-background shadow-sm ring-0",
        "transition-transform duration-200 ease-out",
        "data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
      )}
    />
  </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
